import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../api/axiosInstance";
import { API_ENDPOINTS } from "../../config/apiconfig";
import "../../style/passbook.css";
import ConfirmModal from "../../components/common/ConfirmModal";
import AppDropdown from "../../components/AppDropdown";
import VendorSearch from "../../components/VendorSearch";
import { Box, Typography, Pagination } from "@mui/material";
import { Book as PassbookIcon, Person as PersonIcon, Description as DescriptionIcon, Inventory as InventoryIcon, Receipt as ReceiptIcon } from "@mui/icons-material";

const VendorPassbook = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedVendor, setSelectedVendor] = useState();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: "", type: "info", onConfirm: null });

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: "",
    toDate: "",
  });

  const [vendorSummary, setVendorSummary] = useState([]);
  const [vendorStatement, setVendorStatement] = useState([]);
  const [showVendorStatement, setShowVendorStatement] = useState(false);
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0
  });

  const [vendorInventory, setVendorInventory] = useState([]);

  const [vendorSummaryCards, setVendorSummaryCards] = useState({
    totalOpeningBalance: 0,
    totalClosingBalance: 0,
    uniqueProducts: 0,
    totalRecords: 0,
    lastUpdated: new Date().toLocaleDateString(),
    status: 'Active'
  });

  // Ref to track current request for race condition protection
  const requestRef = useRef(null);
  
  // Ref to track if we've already loaded from URL parameters
  const hasLoadedFromUrl = useRef(false);

  useEffect(() => {
    // Handle navigation state from Dashboard (passbook button click)
    const state = location.state;
    if (state && state.vendorId && !hasLoadedFromUrl.current) {
      const vendorData = {
        id: state.vendorId,
        name: state.vendorName,
        email: state.vendorEmail
      };

      setSelectedVendor(vendorData);

      // Clear empty filters
      const emptyFilters = {
        fromDate: "",
        toDate: ""
      };
      setFilters(emptyFilters);
      setAppliedFilters(emptyFilters);

      hasLoadedFromUrl.current = true;

      // Directly fetch passbook summary with vendor data
const fetchSummary = async () => {
        try {
          const response = await api.get(
            API_ENDPOINTS.VENDOR_PASSBOOK.GET_SUMMARY(vendorData.id),
            {
              params: {
                fromDate: null,
                toDate: null
              }
            }
          );

          const summaryData = response.data || [];
          
          // Transform API response - VendorPassbookSummaryDTO has: openingBalance, closingBalance, inwardQty, outwardQty, productName
          const transformedSummary = summaryData.map(item => ({
            goodsItemId: item.goodsItemId,
            name: item.productName || '—',
            productName: item.productName || '—',
            openingBalance: item.openingBalance || 0,
            closingBalance: item.closingBalance || 0,
            inwardQty: item.inwardQty || 0,
            outwardQty: item.outwardQty || 0
          }));
          
          setVendorSummary(transformedSummary);

          const totalOpeningBalance = transformedSummary.reduce((sum, item) => sum + (item.openingBalance || 0), 0);
          const totalClosingBalance = transformedSummary.reduce((sum, item) => sum + (item.closingBalance || 0), 0);
          
          setVendorSummaryCards({
            totalOpeningBalance: totalOpeningBalance,
            totalClosingBalance: totalClosingBalance,
            uniqueProducts: transformedSummary.length,
            totalRecords: transformedSummary.length,
            lastUpdated: new Date().toLocaleDateString(),
            status: 'Active'
          });
        } catch (error) {
          console.error("Error fetching vendor passbook summary:", error);
        }
      };

      fetchSummary();
      return;
    }
  }, [location.state]);

  /* ---------------- FETCH VENDOR PASSBOOK SUMMARY ---------------- */
  const fetchVendorPassbookSummary = useCallback(async () => {
    // Check if vendor is selected
    if (!selectedVendor) {
      setConfirmModal({ 
        isOpen: true, 
        message: "Please select a vendor first", 
        type: "warning",
        onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
      });
      return;
    }
    
    console.log("Fetching vendor passbook summary for vendor:", selectedVendor.id);
    console.log("Applied filters:", appliedFilters);
    
    // Cancel previous request if exists
    if (requestRef.current) {
      requestRef.current.abort();
    }
    
    // Create new AbortController for this request
    const controller = new AbortController();
    requestRef.current = controller;
    
    // Reset passbook statement view when applying filter
    setShowVendorStatement(false);
    setVendorStatement([]);
    
    try {
      const response = await api.get(
        API_ENDPOINTS.VENDOR_PASSBOOK.GET_SUMMARY(selectedVendor.id),
        {
          params: {
            fromDate: appliedFilters.fromDate || null,
            toDate: appliedFilters.toDate || null
          },
          signal: controller.signal
        }
      );

      console.log("Vendor passbook summary response:", response.data);

const summaryData = response.data || [];
       
       // Transform API response - VendorPassbookSummaryDTO has: openingBalance, closingBalance, inwardQty, outwardQty, productName
       const transformedSummary = summaryData.map(item => ({
         goodsItemId: item.goodsItemId,
         name: item.productName || '—',
         productName: item.productName || '—',
         openingBalance: item.openingBalance || 0,
         closingBalance: item.closingBalance || 0,
         inwardQty: item.inwardQty || 0,
         outwardQty: item.outwardQty || 0
       }));
       
       setVendorSummary(transformedSummary);
       
       // Calculate vendor summary cards - match Customer Passbook
       const totalOpeningBalance = transformedSummary.reduce((sum, item) => sum + (item.openingBalance || 0), 0);
       const totalClosingBalance = transformedSummary.reduce((sum, item) => sum + (item.closingBalance || 0), 0);
       
       setVendorSummaryCards({
         totalOpeningBalance: totalOpeningBalance,
         totalClosingBalance: totalClosingBalance,
         uniqueProducts: transformedSummary.length,
         totalRecords: transformedSummary.length,
         lastUpdated: new Date().toLocaleDateString(),
         status: 'Active'
       });

    } catch (error) {
      // Don't show error if request was cancelled
      if (error.name !== 'CanceledError') {
        console.error("Error fetching vendor passbook summary:", error);
        setConfirmModal({ 
          isOpen: true, 
          message: "Failed to load vendor passbook data", 
          type: "error",
          onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
        });
      }
    } finally {
      requestRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendor?.id, appliedFilters.fromDate, appliedFilters.toDate]);

  // Auto-fetch when vendor is selected (for manual vendor selection via search)
  useEffect(() => {
    if (selectedVendor) {
      // Set flag to true for manual selections
      hasLoadedFromUrl.current = true;
      fetchVendorPassbookSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendor?.id, appliedFilters.fromDate, appliedFilters.toDate]);

  /* ---------------- FETCH VENDOR STATEMENT ---------------- */
  const fetchVendorStatement = async (page = 0) => {
    if (!selectedVendor) {
      setConfirmModal({ 
        isOpen: true, 
        message: "Please select a vendor first", 
        type: "warning",
        onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
      });
      return;
    }
    
    setLoadingStatement(true);
    try {
      const response = await api.get(
        API_ENDPOINTS.VENDOR_PASSBOOK.GET_STATEMENT(selectedVendor.id),
        {
          params: {
            page: page,
            size: 10,
            fromDate: appliedFilters.fromDate || null,
            toDate: appliedFilters.toDate || null
          }
        }
      );
      
      const records = response.data?.content || [];
      setVendorStatement(records);
      setPagination({
        page: response.data?.number || 0,
        size: response.data?.size || 10,
        totalElements: response.data?.totalElements || 0,
        totalPages: response.data?.totalPages || 0
      });
      setShowVendorStatement(true);
      
    } catch (error) {
      console.error("Error fetching vendor statement:", error);
      setConfirmModal({ 
        isOpen: true, 
        message: "Failed to load vendor statement", 
        type: "error",
        onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
      });
    } finally {
      setLoadingStatement(false);
    }
  };

  /* ---------------- FETCH VENDOR INVENTORY ---------------- */
  const fetchVendorInventory = useCallback(async () => {
    if (!selectedVendor) return;
    
    try {
      const response = await api.get(
        API_ENDPOINTS.VENDOR_PASSBOOK.GET_INVENTORY(selectedVendor.id)
      );
      
      const inventoryData = response.data || [];
      setVendorInventory(inventoryData);
      
    } catch (error) {
      console.error("Error fetching vendor inventory:", error);
    }
  }, [selectedVendor]);

  // Auto-fetch inventory when vendor is selected
  useEffect(() => {
    if (selectedVendor) {
      fetchVendorInventory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendor]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  /* ---------------- HANDLE APPLY FILTER ---------------- */
  const handleApplyFilter = () => {
    if (!selectedVendor) {
      setConfirmModal({ 
        isOpen: true, 
        message: "Please select a vendor first", 
        type: "warning",
        onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
      });
      return;
    }

    // Validate date range
    if (filters.fromDate && filters.toDate && filters.fromDate > filters.toDate) {
      setConfirmModal({ 
        isOpen: true, 
        message: "From Date must be before or equal to To Date", 
        type: "warning",
        onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
      });
      return;
    }

    // Update applied filters and trigger API call
    setAppliedFilters({
      fromDate: filters.fromDate,
      toDate: filters.toDate
    });
  };

  /* ---------------- HANDLE RESET ---------------- */
  const handleReset = () => {
    setSelectedVendor(null);
    setFilters({
      fromDate: "",
      toDate: "",
    });
    setAppliedFilters({
      fromDate: "",
      toDate: ""
    });
    setVendorSummary([]);
    setVendorSummaryCards({
      totalOpeningBalance: 0,
      totalClosingBalance: 0,
      uniqueProducts: 0,
      totalRecords: 0,
      lastUpdated: "",
      status: "Active",
    });
    hasLoadedFromUrl.current = false;
    setShowVendorStatement(false);
    setVendorStatement([]);
    setVendorInventory([]);
  };

  /* ---------------- HANDLE VIEW PRODUCT DETAILS ---------------- */
  const handleViewProductDetails = (product) => {
    const goodsItemId = product.goodsItemId;
    if (selectedVendor && goodsItemId) {
      // Use URL parameters for better persistence with applied filters
      const params = new URLSearchParams();
      if (appliedFilters.fromDate) params.append('fromDate', appliedFilters.fromDate);
      if (appliedFilters.toDate) params.append('toDate', appliedFilters.toDate);
      
      const queryString = params.toString();
      const url = queryString 
        ? `/dashboard/purchases/vendor-passbook/product/${selectedVendor.id}/${goodsItemId}?${queryString}`
        : `/dashboard/purchases/vendor-passbook/product/${selectedVendor.id}/${goodsItemId}`;
      
      navigate(url);
    }
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value - 1 }));
    fetchVendorStatement(value - 1);
  };

  const handlePageSizeChange = (newSize) => {
    setPagination(prev => ({ ...prev, size: newSize, page: 0 }));
    fetchVendorStatement(0);
  };

  return (
    <>
      <div className="passbookContainer">
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              bgcolor: "#4338CA",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 8px 20px rgba(67,56,202,.18)"
            }}
          >
            <PassbookIcon sx={{ fontSize: 26 }} />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: 38,
                fontWeight: 700,
                lineHeight: 1.1
              }}
            >
              Vendor Passbook
            </Typography>

            <Typography
              sx={{
                color: "#64748B",
                fontSize: 15
              }}
            >
              View vendor passbook, stock movements and transaction history
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filter Card */}
      <div className="pb-card">
        <div className="pb-grid">
          <div className="pb-field">
            <VendorSearch 
              onSelectVendor={setSelectedVendor} 
              selectedVendor={selectedVendor} 
            />
          </div>

          <div className="pb-field">
            <label>From Date</label>
            <input
              type="date"
              name="fromDate"
              value={filters.fromDate}
              onChange={handleChange}
            />
          </div>

          <div className="pb-field">
            <label>To Date</label>
            <input
              type="date"
              name="toDate"
              value={filters.toDate}
              onChange={handleChange}
            />
          </div>

          <div className="pb-field pb-field-actions">
            <label>&nbsp;</label>
            <div className="pb-button-group">
              <button className="pb-btn-outline pb-btn-responsive" onClick={handleReset}>
                Clear
              </button>
              <button className="pb-btn-primary pb-btn-responsive" onClick={handleApplyFilter}>
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {!selectedVendor && (
        <div className="pb-card pb-empty">
          <PersonIcon sx={{ fontSize: 64, color: '#CBD5E1' }} />
          <h3>Please select a vendor to view passbook details</h3>
        </div>
      )}

{/* Vendor Summary Cards */}
      {selectedVendor && (
        <div className="pb-card">
          <div className="pb-vendor-summary">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3><PersonIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} /> Vendor Summary</h3>
              <span className="status-active">{vendorSummaryCards.status}</span>
            </div>
            <p className="vendor-name">{selectedVendor.name || selectedVendor.displayName}</p>
            {selectedVendor.companyName && <p className="vendor-company">{selectedVendor.companyName}</p>}
            {selectedVendor.gstin && <p className="vendor-gstin">GSTIN: {selectedVendor.gstin}</p>}
            {selectedVendor.email && <p className="vendor-email">Email: {selectedVendor.email}</p>}
            {selectedVendor.phone && <p className="vendor-phone">Phone: {selectedVendor.phone}</p>}
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">Total Opening Balance</span>
                <span className="summary-value">{vendorSummaryCards.totalOpeningBalance}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Closing Balance</span>
                <span className="summary-value">{vendorSummaryCards.totalClosingBalance}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Unique Products</span>
                <span className="summary-value">{vendorSummaryCards.uniqueProducts}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Records</span>
                <span className="summary-value">{vendorSummaryCards.totalRecords}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Last Updated</span>
                <span className="summary-value">{vendorSummaryCards.lastUpdated}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Status</span>
                <span className="summary-value status-active">{vendorSummaryCards.status}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Inventory Card */}
      {vendorInventory.length > 0 && (
        <div className="pb-card">
          <div className="pb-product-summary">
            <h3><InventoryIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} /> Vendor Inventory</h3>
            <p className="product-count">Showing {vendorInventory.length} products in inventory</p>
            <div className="pb-table-wrapper responsive-table-container">
              <table className="passbook-table responsive-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Inward Qty</th>
                    <th>Outward Qty</th>
                    <th>Balance Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorInventory.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <span style={{ fontWeight: '600', color: '#0f172a' }}>{item.goodsName || item.productName || '—'}</span>
                      </td>
                      <td>
                        <span style={{
                          background: '#dcfce7',
                          color: '#15803d',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {item.qtyInward}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          background: '#dbeafe',
                          color: '#2563eb',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {item.qtyOutward}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          background: item.qtyBalance >= 0 ? '#fef3c7' : '#fee2e2',
                          color: item.qtyBalance >= 0 ? '#d97706' : '#dc2626',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {item.qtyBalance}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Product Summary Card */}
      {vendorSummary.length > 0 && (
        <div className="pb-card">
          <div className="pb-product-summary">
            <h3><DescriptionIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} /> Product Summary</h3>
            <p className="product-count">Showing {vendorSummary.length} unique products</p>
            <div className="pb-table-wrapper responsive-table-container">
              <table className="passbook-table responsive-table">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Opening Balance</th>
                    <th>Closing Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorSummary.map((product, index) => (
                    <tr key={index}>
                      <td>
                        <span style={{ fontWeight: '600', color: '#0f172a' }}>{product.name || product.productName || '—'}</span>
                      </td>
                      <td>
                        <span style={{
                          background: product.openingBalance >= 0 ? '#f1f5f9' : '#fee2e2',
                          color: product.openingBalance >= 0 ? '#475569' : '#dc2626',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {product.openingBalance ?? 0}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          background: product.closingBalance >= 0 ? '#f1f5f9' : '#fee2e2',
                          color: product.closingBalance >= 0 ? '#475569' : '#dc2626',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {product.closingBalance ?? 0}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => handleViewProductDetails(product)}
                          style={{
                            background: '#4338ca',
                            color: '#fff',
                            border: 'none',
                            padding: '6px 16px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#3730a3'}
                          onMouseOut={(e) => e.target.style.background = '#4338ca'}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
)}

      {/* View Vendor Statement Button */}
      {vendorSummary.length > 0 && !showVendorStatement && (
        <div style={{ padding: '24px 32px', textAlign: 'center' }}>
          <button 
            className="pb-btn-primary pb-btn-responsive"
            onClick={() => fetchVendorStatement(0)}
            style={{ minWidth: '200px' }}
          >
            View Vendor Statement
          </button>
        </div>
      )}

      {/* Vendor Statement Card */}
      {showVendorStatement && (
        <div className="pb-card">
          <div className="pb-product-summary">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3><ReceiptIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} /> Vendor Statement</h3>
              <button
                className="pb-btn-outline pb-btn-responsive"
                onClick={() => setShowVendorStatement(false)}
              >
                Hide
              </button>
            </div>
            <p className="product-count">{pagination.totalElements} Records • Page {pagination.page + 1} of {pagination.totalPages}</p>
            
            <div className="pb-table-wrapper responsive-table-container">
              <table className="passbook-table responsive-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Transaction No.</th>
                    <th>Product</th>
                    <th>Type</th>
                    <th>Opening</th>
                    <th>Inward</th>
                    <th>Outward</th>
                    <th>Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingStatement ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                        Loading records...
                      </td>
                    </tr>
                  ) : vendorStatement.length === 0 ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                        No records found
                      </td>
                    </tr>
                  ) : (
                    vendorStatement.map((record, index) => {
                      return (
                        <tr key={index}>
                          <td>{record.purchaseDate}</td>
                          <td>{record.purchaseNumber}</td>
                          <td>{record.productName}</td>
                          <td>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              ...(record.purchaseType === 'INWARD' && { background: '#DCFCE7', color: '#15803D' }),
                              ...(record.purchaseType === 'OUTWARD' && { background: '#DBEAFE', color: '#2563EB' }),
                            }}>
                              {record.purchaseType}
                            </span>
                          </td>
                          <td>{record.openingBalance}</td>
                          <td style={{color: '#15803d', fontWeight: '600'}}>{record.inwardQty}</td>
                          <td style={{color: '#2563eb', fontWeight: '600'}}>{record.outwardQty}</td>
                          <td style={{fontWeight: '600'}}>{record.closingBalance}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loadingStatement && pagination.totalPages > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    md: "row",
                  },
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  p: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "center", md: "flex-start" }, gap: 1 }}>
                  {pagination.totalElements > 0 && (
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                      Showing {pagination.page * pagination.size + 1}–{Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of {pagination.totalElements} records
                    </Typography>
                  )}
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.page + 1}
                    onChange={handlePageChange}
                    disabled={loadingStatement}
                    shape="rounded"
                    variant="outlined"
                    color="primary"
                    showFirstButton
                    showLastButton
                    siblingCount={0}
                    boundaryCount={1}
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        transition: "transform .2s, box-shadow .2s, background-color .2s",
                        borderRadius: "10px",
                        fontWeight: 600,
                        minWidth: 38,
                        height: 38,
                      },
                      "& .MuiPaginationItem-root:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 10px rgba(0,0,0,.15)",
                      },
                      "& .MuiPaginationItem-root:active": {
                        transform: "scale(.95)",
                      },
                      "& .Mui-selected": {
                        backgroundColor: "#4338CA",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#3730a3",
                        },
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    width: {
                      xs: 150,
                      sm: 130,
                    },
                    transition: "all .2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500, color: "#475569", mb: 0.75 }}>Rows per page</Typography>
                  <AppDropdown
                    value={pagination.size}
                    options={[
                      { value: 5, label: '5' },
                      { value: 10, label: '10' },
                      { value: 25, label: '25' },
                      { value: 50, label: '50' }
                    ]}
                    onChange={handlePageSizeChange}
                    disabled={loadingStatement}
                  />
                </Box>
              </Box>
            )}
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onConfirm={confirmModal.onConfirm || (() => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null }))}
        onCancel={() => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })}
        title={confirmModal.type === 'error' ? 'Error' : confirmModal.type === 'warning' ? 'Warning' : confirmModal.type === 'success' ? 'Success' : 'Information'}
        message={confirmModal.message}
        confirmText="OK"
        cancelText=""
        type={confirmModal.type === 'error' ? 'danger' : confirmModal.type}
      />
      </div>
    </>
  );
};

export default VendorPassbook;